import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { AlertCircle, CheckCircle, Pause, Play } from "lucide-react";
import { useV2ProtocolData, useV2Accounts } from "~/hooks/useV2Data";

interface V2ProtocolMetricsProps {
  // No props needed - data fetched internally
}

export function V2ProtocolMetrics({}: V2ProtocolMetricsProps) {
  const { data: protocolData, isLoading: protocolLoading, error: protocolError } = useV2ProtocolData();
  const { data: accounts, isLoading: accountsLoading } = useV2Accounts();

  if (protocolLoading || accountsLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (protocolError || !protocolData) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Failed to load protocol data</p>
        </div>
      </div>
    );
  }

  const totalAccounts = accounts?.length || 0;
  const activePositions = accounts?.filter(account => account.debt > 0).length || 0;
  const formatUSD = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatGOHM = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  // Calculate average LTV across all accounts instead of utilization
  const averageLTV = accounts && accounts.length > 0 
    ? accounts.reduce((sum, account) => sum + account.ltv, 0) / accounts.length 
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Protocol Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Collateral</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatGOHM(protocolData.totalCollateral)} gOHM</div>
              <p className="text-xs text-muted-foreground">
                Locked in protocol
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Debt</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatUSD(protocolData.totalDebt)}</div>
              <p className="text-xs text-muted-foreground">
                Outstanding stablecoin
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Interest Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatPercentage(protocolData.interestRate)}</div>
              <p className="text-xs text-muted-foreground">
                Annual rate
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Account Activity</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Accounts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalAccounts.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                Unique accounts with activity
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Positions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activePositions.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                Positions with outstanding debt
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Protocol Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Borrowing Status</CardTitle>
              {protocolData.borrowsPaused ? (
                <Pause className="h-4 w-4 text-orange-500" />
              ) : (
                <Play className="h-4 w-4 text-green-500" />
              )}
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  protocolData.borrowsPaused 
                    ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300" 
                    : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                }`}>
                  {protocolData.borrowsPaused ? "Paused" : "Active"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {protocolData.borrowsPaused ? "New borrows are paused" : "New borrows are allowed"}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Liquidation Status</CardTitle>
              {protocolData.liquidationsPaused ? (
                <Pause className="h-4 w-4 text-orange-500" />
              ) : (
                <CheckCircle className="h-4 w-4 text-green-500" />
              )}
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  protocolData.liquidationsPaused 
                    ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300" 
                    : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                }`}>
                  {protocolData.liquidationsPaused ? "Paused" : "Active"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {protocolData.liquidationsPaused ? "Liquidations are paused" : "Liquidations are active"}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 